import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { Absorber } from "../../../../../../http/vertigrip/types/absorbers";
import { AdminInstallationType } from "../../../../../../http/vertigrip/types/installationTypes";
import { useAbsorbersApi } from "../../../../../../http/vertigrip/useAbsorbers";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import ModalDialog from "../../../../../ui/ModalDialog";
import Numeric from "../../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface UpdateAbsorberForm {
  installationType: AdminInstallationType | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface EditAbsorberModalDialogProps {
  absorber?: Absorber;
  onClose: () => void;
}

const EditAbsorberModalDialog: FC<EditAbsorberModalDialogProps> = ({
  absorber,
  onClose,
}: EditAbsorberModalDialogProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const absorbersApi = useAbsorbersApi();

  return (
    <>
      {!!absorber && (
        <ModalDialog onClose={() => onClose()} title={i18n.translation.absorbers.edit}>
          <Form<UpdateAbsorberForm>
            defaultValues={{
              installationType: null,
              order: absorber?.order ?? 50,
              visibility: absorber?.visibility,
            }}
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={() => (formData) =>
              absorbersApi
                .updateAdminAbsorber(absorber?.id!, {
                  order: formData.order!,
                  steelVersionId: absorber?.vertigripSteelVersion.id!,
                  visibility: formData.visibility,
                })
                .then(() => {
                  onClose();
                  dispatch(showSuccessNotification(i18n.translation));
                })
                .catch((err) => {
                  dispatch(showErrorNotification(i18n, err));
                  setIsSubmitting(false);
                })
            }
          >
            {({ control, setValue }) => {
              return (
                <FormFieldsContainer fullWidth>
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                        {({ labelId }) => (
                          <VisibilityScope
                            {...field}
                            inputId={labelId}
                            onChange={(value) => setValue("visibility", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="order"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.order} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            decimalScale={0}
                            allowNegative={false}
                            onBlur={(value) => setValue("order", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default EditAbsorberModalDialog;
